import { format, eachDayOfInterval, startOfDay, endOfMonth } from 'date-fns';
import { findCampaignMatch } from '../../../lib/OriginResolver';
import { BoardEntity, BoardChannel } from '../../../../../stores/boardStore';

/**
 * Extracts and categorizes revenue sources based on transaction traffic data.
 * Can be used for a single entity or multiple entities over a given date range.
 * 
 * @param finance The finance data containing pre-aggregated daily breakdown.
 * @param entities The list of board entities.
 * @param channels The list of board channels.
 * @param dateRange The date range to filter.
 * @param t Translation function.
 * @param entityId Optional single entity ID to filter by.
 * @returns An array of metric mappings with their respective links and matches.
 */
export const calculateRevenueSourceBreakdown = (
    finance: any,
    entities: BoardEntity[] | undefined,
    channels: BoardChannel[] | undefined,
    dateRange: { startDate: Date | null; endDate: Date | null },
    t: (key: string, options?: any) => string,
    entityId?: string
) => {
    const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfDay(new Date());
    const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
    const daySet = new Set(eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd')));

    const dailyData = finance?.dailyBreakdown || {};
    const activities: any[] = [];

    // Flatten daily breakdown items for processing
    Object.entries(dailyData).forEach(([dateStr, dayData]: [string, any]) => {
        if (!daySet.has(dateStr)) return;
        dayData.breakdown.forEach((item: any) => {
            if (item.type !== 'REVENUE') return;
            if (entityId && item.entityId !== entityId) return;
            activities.push({
                ...item,
                date: dateStr,
                amount: item.value
            });
        });
    });

    const groupsMap: Record<string, {
        name: string;
        totalRevenue: number;
        links: Array<{
            source: string;
            isPaid: boolean;
            revenue: number;
            matches: Array<{
                id: string;
                name: string;
                revenue: number;
                externalId?: string;
                sourceUrls?: string[];
                isSelected: boolean;
                isEnabled: boolean;
                channelId?: string;
                channelName?: string;
                isDisconnected?: boolean;
                isMatched: boolean;
                isOrganic: boolean;
                entityType?: string;
                parentId?: string;
                originUrls?: string[];
            }>;
        }>
    }> = {};

    activities.forEach((activity: any) => {
        const traffic = activity.traffic || {};
        const sourceName = traffic.target || t('pages.traffic.modals.platforms.directOrganic', { defaultValue: 'Direct / Organic' });
        const sourceId = traffic.source ? String(traffic.source) : null;
        const isPaid = traffic.isPaid === true;

        const metric = entities?.find((e: any) => e.id === activity.entityId);
        const metricName = metric?.name || activity.entityName || t('pages.traffic.modals.generalSales', { defaultValue: 'General Sales' });

        if (!groupsMap[metricName]) {
            groupsMap[metricName] = { name: metricName, totalRevenue: 0, links: [] };
        }

        groupsMap[metricName].totalRevenue += Number(activity.amount) || 0;

        let link = groupsMap[metricName].links.find(l => l.source === sourceName && l.isPaid === isPaid);
        if (!link) {
            link = { source: sourceName, isPaid, revenue: 0, matches: [] };
            groupsMap[metricName].links.push(link);
        }
        const amount = Number(activity.amount) || 0;
        link.revenue += amount;

        if (isPaid) {
            const matchedEntity = findCampaignMatch(traffic, entities);

            const primaryId = traffic.ad_id || traffic.adset_id || traffic.campaign_id || traffic.source || 'no-id';
            const matchId = matchedEntity ? matchedEntity.id : `unmatched-${sourceName}-${primaryId}`;

            let match: any = link.matches.find(m => m.id === matchId);

            if (!match) {
                match = {
                    id: matchId,
                    name: matchedEntity ? matchedEntity.name : (sourceId ? t('pages.traffic.modals.unmatchedSourceWithId', { source: sourceName, id: sourceId, defaultValue: `Unmatched Source: ${sourceName} (${sourceId})` }) : t('pages.traffic.modals.unmatchedSource', { source: sourceName, defaultValue: `Unmatched Source: ${sourceName}` })),
                    revenue: 0,
                    externalId: matchedEntity?.externalId || sourceId || undefined,
                    sourceUrls: matchedEntity?.feed?.source,
                    isSelected: !!matchedEntity?.isSelected,
                    isEnabled: matchedEntity?.isEnabled !== false,
                    channelId: matchedEntity?.channelId,
                    isMatched: !!matchedEntity,
                    isOrganic: false,
                    entityType: matchedEntity?.entityType,
                    parentId: matchedEntity?.parentId,
                    originUrls: matchedEntity?.feed?.origin
                };

                // Walk up to the root so we can find the matching pseudo-channel
                // BoardChannel row (for Meta/TikTok ad accounts).
                let rootEntity = matchedEntity;
                if (matchedEntity) {
                    let current = matchedEntity;
                    let failsafe = 0;
                    while (current && current.parentId && failsafe < 50) {
                        failsafe++;
                        const parent = entities?.find((e: any) => String(e.id) === String(current.parentId));
                        if (parent) {
                            current = parent;
                        } else {
                            break;
                        }
                    }
                    rootEntity = current;
                }

                if (match.channelId) {
                    // Prefer the pseudo-channel row whose source entity is the
                    // walked-up root; fall back to a normal channel match.
                    const channel = channels?.find((c: any) =>
                        c.isPseudoChannel
                            ? rootEntity && c.sourceEntityId === rootEntity.id
                            : String(c.channelId ?? c.id) === String(match.channelId),
                    );
                    match.isDisconnected = channel?.status === 'disconnected' || channel?.isEnabled === false;
                    match.channelName = channel?.name;
                }

                link.matches.push(match);
            }
            match.revenue += amount;
        } else {
            const matchId = `organic-${sourceName}`;
            let match: any = link.matches.find(m => m.id === matchId);
            if (!match) {
                match = {
                    id: matchId,
                    name: t('pages.traffic.table.organic', { defaultValue: 'Organic' }),
                    revenue: 0,
                    isSelected: true,
                    isEnabled: true,
                    isMatched: true,
                    isOrganic: true
                };
                link.matches.push(match);
            }
            match.revenue += amount;
        }
    });

    const sorted = Object.values(groupsMap)
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .map(g => ({
            ...g,
            links: g.links.sort((a, b) => b.revenue - a.revenue).map(l => ({
                ...l,
                matches: l.matches.sort((a, b) => b.revenue - a.revenue)
            }))
        }));

    return sorted;
};

export const calculatePlatformAttribution = (
    finance: any,
    dateRange: { startDate: Date | null; endDate: Date | null },
    t: (key: string, options?: any) => string
) => {
    const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfDay(new Date());
    const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
    const daySet = new Set(eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd')));

    const dailyData = finance?.dailyBreakdown || {};
    const activities: any[] = [];

    Object.entries(dailyData).forEach(([dateStr, dayData]: [string, any]) => {
        if (!daySet.has(dateStr)) return;
        dayData.breakdown.forEach((item: any) => {
            activities.push({ ...item, date: dateStr, amount: item.value });
        });
    });

    const sourcesMap: Record<string, {
        paid: Record<string, number>,
        organic: Record<string, number>,
        total: number,
        paidTotal: number,
        organicTotal: number
    }> = {};
    let totalRevenue = 0;
    
    activities.forEach((activity: any) => {
        if (!daySet.has(activity.date)) return;
        const traffic = activity.traffic || {};
        const target = traffic.target || t('pages.traffic.modals.platforms.directOrganic', { defaultValue: 'Direct / Organic' });
        const isPaid = traffic.isPaid === true;
        const amount = Number(activity.amount) || 0;

        if (activity.type === 'REVENUE') {
            totalRevenue += amount;
            if (!sourcesMap[target]) {
                sourcesMap[target] = { paid: {}, organic: {}, total: 0, paidTotal: 0, organicTotal: 0 };
            }
            const category = isPaid ? 'paid' : 'organic';
            const catTotalKey = isPaid ? 'paidTotal' : 'organicTotal';
            sourcesMap[target][category][activity.date] = (sourcesMap[target][category][activity.date] || 0) + amount;
            sourcesMap[target][catTotalKey] += amount;
            sourcesMap[target].total += amount;
        }
    });

    const PRIORITY_SOURCES = ['Meta', 'Facebook', 'Instagram', 'Google', 'TikTok', 'Pinterest', 'Snapchat', 'X', 'LinkedIn'];

    const barSources = Object.keys(sourcesMap).map(name => {
        const data = sourcesMap[name];
        return {
            name,
            revenue: data.total,
            paidRevenue: data.paidTotal,
            organicRevenue: data.organicTotal,
            attribution: totalRevenue > 0 ? (data.total / totalRevenue) * 100 : 0,
            paidAttribution: totalRevenue > 0 ? (data.paidTotal / totalRevenue) * 100 : 0,
            organicAttribution: totalRevenue > 0 ? (data.organicTotal / totalRevenue) * 100 : 0
        };
    }).sort((a, b) => {
        const getPriority = (name: string) => {
            const index = PRIORITY_SOURCES.findIndex(p => name.toLowerCase().includes(p.toLowerCase()));
            return index === -1 ? PRIORITY_SOURCES.length : index;
        };
        const priorityA = getPriority(a.name);
        const priorityB = getPriority(b.name);
        if (priorityA !== priorityB) return priorityA - priorityB;
        return b.revenue - a.revenue;
    });

    return { barSources, totalRevenue };
};
